import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Check, ArrowUpRight, Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";
import OrderModal from "@/components/OrderModal";

const COMBO = {
    title: "Mastery & Strength Combo",
    price: 24.80,
    original: 34.80,
    tagline: "Ball Mastery + Core Strength",
};

export default function ProgramDetailPage() {
    const { t } = useLang();
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [orderOpen, setOrderOpen] = useState(false);
    const [comboOpen, setComboOpen] = useState(false);

    useEffect(() => {
        api.get(`/programs/${id}`).then((r) => setP(r.data))
            .catch(() => setP(null)).finally(() => setLoading(false));
        api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    }, [id]);

    if (loading) return <div className="px-6 md:px-20 py-24 text-volt font-heading tracking-widest">LOADING…</div>;
    if (!p) return <div className="px-6 md:px-20 py-24 text-white/60">Program not found.</div>;

    // Cross-sell logic: on Core Strength show Ball Mastery, on Ball Mastery show Core Strength
    const otherTitle = p.title === "Core Strength" ? "Ball Mastery"
                    : p.title === "Ball Mastery" ? "Core Strength" : null;
    const other = otherTitle ? programs.find((x) => x.title === otherTitle) : null;
    const showCombo = ["Core Strength", "Ball Mastery"].includes(p.title);

    return (
        <div data-testid="program-detail-page" className="px-6 md:px-12 lg:px-20 py-16">
            <Link to="/programs" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-volt mb-8">
                <ArrowLeft size={14} /> {t("back_to_programs")}
            </Link>

            {/* Hero */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="aspect-video bg-black overflow-hidden">
                    <img src={p.hero_image_url || p.thumbnail_url} alt={p.title}
                         className="w-full h-full object-contain" />
                </div>
                <div>
                    <div className="flex items-center gap-3 flex-wrap mb-4">
                        <span className="text-xs uppercase tracking-widest text-volt bg-volt/10 border border-volt/30 px-3 py-1">
                            {p.duration_weeks} {t("weeks")}
                        </span>
                        <span className="text-xs uppercase tracking-widest text-white/60 border border-white/10 px-3 py-1">
                            {t("age")}: {p.age_min}+
                        </span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-heading uppercase">{p.title}</h1>
                    {p.slogan && (
                        <div className="mt-3 text-volt font-heading text-lg tracking-wider">{p.slogan}</div>
                    )}
                    <div className="mt-6 text-white/70 leading-relaxed">
                        <p>{p.description}</p>
                        {p.long_description && (
                            <>
                                {expanded && <p className="mt-4">{p.long_description}</p>}
                                <button
                                    data-testid="read-more-toggle"
                                    onClick={() => setExpanded((v) => !v)}
                                    className="mt-3 text-xs uppercase tracking-widest text-volt hover:underline">
                                    {expanded ? t("read_less") : t("read_more")} →
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Info sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
                {/* Program will improve */}
                <div className="border border-white/10 bg-zinc-900 p-6">
                    <div className="font-heading text-volt tracking-widest text-sm">
                        {t("program_will_improve")}
                    </div>
                    <ul className="mt-4 space-y-2.5">
                        {p.goals?.map((g, i) => (
                            <li key={i} className="flex gap-2 items-start text-sm">
                                <Check size={14} className="text-volt mt-1 shrink-0" />
                                <span className="text-white/80">{g}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                {/* Program includes */}
                <div className="border border-white/10 bg-zinc-900 p-6">
                    <div className="font-heading text-volt tracking-widest text-sm">
                        {t("program_includes")}
                    </div>
                    <ul className="mt-4 space-y-2.5">
                        {p.includes?.map((g, i) => (
                            <li key={i} className="flex gap-2 items-start text-sm">
                                <span className="text-volt mt-0.5">•</span>
                                <span className="text-white/80">{g}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Week structure */}
            {p.week_structure?.length > 0 && (
                <div className="border border-white/10 bg-zinc-900 p-6 mt-5">
                    <div className="font-heading text-volt tracking-widest text-sm">
                        {(p.duration_weeks || p.week_structure.length)}-WEEK STRUCTURE
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {p.week_structure.map((w, i) => (
                            <div key={i} className="border border-white/10 p-3 text-center">
                                <div className="font-heading text-lg">{w}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Reviews link */}
            <div className="mt-10 text-center">
                <Link to="/reviews#programs" data-testid="reviews-link"
                      className="inline-flex items-center gap-2 text-white/80 hover:text-volt underline underline-offset-4 decoration-white/30 hover:decoration-volt text-sm uppercase tracking-widest">
                    {t("check_reviews")} <ArrowUpRight size={14} />
                </Link>
            </div>

            {/* Price + Buy · or Contact-the-coach for Individual Training Plan */}
            {p.price_eur > 0 ? (
                <div className="mt-10 border border-volt/40 bg-volt/5 p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/60">Price</div>
                        <div className="font-heading text-5xl text-volt mt-1">
                            {p.price_eur?.toFixed(2)} €
                        </div>
                        <div className="text-xs text-white/50 mt-1">One-time payment · Lifetime access</div>
                    </div>
                    <button
                        data-testid="buy-program-btn"
                        onClick={() => setOrderOpen(true)}
                        disabled={!p.is_available}
                        className="btn-volt px-10 py-4 text-base inline-flex items-center gap-2 disabled:opacity-60">
                        {t("buy_program")} <ArrowUpRight size={16} />
                    </button>
                </div>
            ) : (
                <div className="mt-10 border border-volt/40 bg-volt/5 p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/60">Custom Pricing</div>
                        <div className="font-heading text-3xl mt-1">Individual quote</div>
                        <div className="text-xs text-white/50 mt-1">Based on your goals & duration</div>
                    </div>
                    <a href="https://wa.me/4917656759019" target="_blank" rel="noreferrer"
                       data-testid="contact-coach-btn"
                       className="btn-volt px-10 py-4 text-base inline-flex items-center gap-2">
                        Contact the coach <ArrowUpRight size={16} />
                    </a>
                </div>
            )}

            <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} program={p} />
            <OrderModal open={comboOpen} onClose={() => setComboOpen(false)} combo={COMBO} />

            {/* Cross-sell + Combo (only for Ball Mastery / Core Strength) */}
            {showCombo && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Upsell frame */}
                    {other && (
                        <Link to={`/programs/${other.id}`}
                              data-testid="upsell-card"
                              className="group border border-white/15 bg-zinc-900 hover:border-volt/50 transition-all p-5 flex items-center gap-4">
                            <div className="w-20 h-20 shrink-0 bg-black overflow-hidden">
                                <img src={other.thumbnail_url} alt={other.title}
                                     className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-widest text-volt font-heading">
                                    –15% AFTER PURCHASING {p.title.toUpperCase()}
                                </div>
                                <div className="font-heading text-xl uppercase mt-1">{other.title}</div>
                                <div className="text-xs text-white/60 mt-1">
                                    Buy now with your promo code emailed after purchase.
                                </div>
                            </div>
                            <ArrowUpRight size={18} className="text-white/50 group-hover:text-volt shrink-0" />
                        </Link>
                    )}

                    {/* Combo card */}
                    <button
                        onClick={() => setComboOpen(true)}
                        data-testid="combo-card"
                        className="group text-left border border-volt/50 bg-gradient-to-br from-volt/10 via-zinc-900 to-black p-5 flex items-center gap-4 hover:border-volt transition-all">
                        <div className="w-20 h-20 shrink-0 bg-black flex items-center justify-center border border-volt/40">
                            <Sparkles size={28} className="text-volt" />
                        </div>
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-volt font-heading">
                                COMBO · SAVE 10€
                            </div>
                            <div className="font-heading text-xl uppercase mt-1">
                                Ball Mastery + Core Strength
                            </div>
                            <div className="text-sm text-white/70 mt-1 flex items-baseline gap-2">
                                <span className="line-through text-white/40">34.80 €</span>
                                <span className="text-volt font-heading text-lg">24.80 €</span>
                            </div>
                        </div>
                        <ArrowUpRight size={18} className="text-volt shrink-0" />
                    </button>
                </div>
            )}
        </div>
    );
}
